import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { tokens } from "../../styles/tokens";
import type { AlertRecord } from "../../stores/telemetryStore";
import { useTelemetryStore } from "../../stores/telemetryStore";

const BEAM_HEIGHT = 10;
const PULSE_PERIOD = Math.PI * 2 / 1.2;

const SEV_COLOR: Record<AlertRecord["severity"], string> = {
  CRIT: tokens.semantic.danger,
  WARN: tokens.semantic.warn,
  INFO: tokens.semantic.info,
};

function buildFadeTexture(): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 4;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  const grad = ctx.createLinearGradient(0, 64, 0, 0);
  grad.addColorStop(0, "rgba(255,255,255,0.85)");
  grad.addColorStop(1, "rgba(255,255,255,0.0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 4, 64);
  return new THREE.CanvasTexture(canvas);
}

interface Props {
  alert: AlertRecord;
  x: number;
  z: number;
}

export function AlertBeam({ alert, x, z }: Props) {
  const matRef     = useRef<THREE.MeshBasicMaterial>(null);
  const select     = useTelemetryStore((s) => s.select);
  const isSelected = useTelemetryStore((s) => s.selectedAssetId === alert.id);

  const fadeTexture = useMemo(() => buildFadeTexture(), []);
  const color = SEV_COLOR[alert.severity] ?? tokens.semantic.info;

  useFrame(({ clock }) => {
    if (!matRef.current) return;
    const t = (Math.sin(clock.elapsedTime * PULSE_PERIOD) + 1) * 0.5;
    matRef.current.opacity = 0.4 + t * 0.4;
  });

  return (
    <group position={[x, BEAM_HEIGHT / 2, z]}>
      <mesh
        onClick={(e) => { e.stopPropagation(); select(alert.id); }}
        renderOrder={1}
      >
        <cylinderGeometry args={[0.1, 0.1, BEAM_HEIGHT, 8, 1, true]} />
        <meshBasicMaterial
          ref={matRef}
          color={color}
          alphaMap={fadeTexture}
          transparent
          opacity={0.6}
          side={THREE.DoubleSide}
          depthWrite={false}
        />
      </mesh>

      {isSelected && (
        <mesh position={[0, -(BEAM_HEIGHT / 2 - 0.05), 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.2, 0.28, 24]} />
          <meshBasicMaterial color={color} toneMapped={false} side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}
